import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { MutationConfig, QueryConfig } from '@/app/lib/tanstack-query';
import { createProject, deleteProject, getProject, getProjects, updateProject } from '@/app/network/api';

export const useCreateProject = ({ mutationConfig }: { mutationConfig?: MutationConfig<typeof createProject> } = {}) => {
  const queryClient = useQueryClient();

  const { onSuccess, ...restConfig } = mutationConfig || {};

  return useMutation({
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({
        queryKey: getProjectsQueryOptions().queryKey
      });

      onSuccess?.(...args);
    },
    ...restConfig,
    mutationFn: createProject
  });
};

export const useDeleteProject = ({ mutationConfig }: { mutationConfig?: MutationConfig<typeof deleteProject> } = {}) => {
  const { onSuccess, ...restConfig } = mutationConfig || {};
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: (data, variables, context) => {
      queryClient.removeQueries({
        queryKey: getProjectQueryOptions(variables.projectId).queryKey
      });

      void queryClient.invalidateQueries({ queryKey: getProjectsQueryOptions().queryKey });
      onSuccess?.(data, variables, context);
    },
    ...restConfig,
    mutationFn: deleteProject
  });
};

export function getProjectQueryOptions(projectId: number) {
  return queryOptions({
    queryKey: ['projects', projectId],
    queryFn: () => getProject({ projectId })
  });
}

export function useProject({ projectId, queryConfig }: { projectId: number; queryConfig?: QueryConfig<typeof getProjectQueryOptions> }) {
  return useQuery({
    ...getProjectQueryOptions(projectId),
    ...queryConfig
  });
}

export function getProjectsQueryOptions() {
  return queryOptions({
    queryKey: ['projects'],
    queryFn: () => getProjects()
  });
}

export function useProjects({ queryConfig }: { queryConfig?: QueryConfig<typeof getProjectsQueryOptions> } = {}) {
  return useQuery({
    ...getProjectsQueryOptions(),
    ...queryConfig
  });
}

export const useUpdateProject = ({ mutationConfig }: { mutationConfig?: MutationConfig<typeof updateProject> } = {}) => {
  const queryClient = useQueryClient();

  const { onSuccess, ...restConfig } = mutationConfig || {};

  return useMutation({
    onSuccess: async (...args) => {
      await queryClient.invalidateQueries({
        queryKey: getProjectsQueryOptions().queryKey
      });

      onSuccess?.(...args);
    },
    ...restConfig,
    mutationFn: updateProject
  });
};
